import { createTheme } from '@mui/material/styles';
import { brand, gradients, surfaces, dividers, states, ink } from './colors';

export const getTheme = (mode: 'light' | 'dark') => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,

      primary: {
        main: isDark ? brand.pink : brand.pinkInk,
      },

      secondary: {
        main: isDark ? brand.white : ink.icon,
      },

      ...(!isDark && {
        text: {
          primary: ink.primary,
          secondary: ink.secondary,
        },
        action: {
          hover: 'rgba(80, 60, 140, 0.06)',
        },
      }),

      background: {
        default: 'transparent',
        paper:
          isDark
            ? surfaces.darkPaper
            : surfaces.lightPaper,
      },

      divider:
        isDark
          ? dividers.dark
          : dividers.light,
    },

    shape: {
      borderRadius: 8,
    },

    typography: {
      fontFamily: `'Nunito', system-ui, -apple-system, BlinkMacSystemFont, sans-serif`,

      h6: {
        fontFamily: `'Indie Flower', cursive`,
        fontWeight: 600,
        letterSpacing: '0.02em',
      },

      h5: {
        fontFamily: `'Indie Flower', cursive`,
        fontWeight: 600,
      },
    },

    components: {
      // index.css paints html/body dark for iOS overscroll; match the mode
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            backgroundColor: isDark ? '#050611' : surfaces.lightGround,
          },
        },
      },

      /**
       * ─────────────────────────────
       * SURFACES (Cards / Panels)
       * ─────────────────────────────
       */
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor:
              isDark
                ? surfaces.darkPaper
                : surfaces.lightPaper,

            backgroundImage:
              isDark
                ? `
                  radial-gradient(
                    400px 260px at 20% 20%,
                    rgba(56,189,248,0.45),
                    transparent 60%
                  ),
                  radial-gradient(
                    380px 280px at 80% 30%,
                    rgba(139,92,246,0.40),
                    transparent 65%
                  ),
                  radial-gradient(
                    420px 320px at 50% 85%,
                    rgba(236,72,153,0.35),
                    transparent 70%
                  )
                `
                : 'none',

            backgroundBlendMode: 'screen',

            // Daylight Glass: frosted white over the light aurora, tinted with a
            // softer copy of the dark Paper's glows. Blur is desktop-only;
            // phones get a more opaque surface instead.
            ...(!isDark && {
              backgroundImage: `
                radial-gradient(400px 260px at 20% 20%, rgba(56,189,248,0.22), transparent 60%),
                radial-gradient(380px 280px at 80% 30%, rgba(139,92,246,0.19), transparent 65%),
                radial-gradient(420px 320px at 50% 85%, rgba(236,72,153,0.18), transparent 70%)
              `,
              backgroundBlendMode: 'normal',
              backgroundColor: surfaces.lightPaperSolid,
              border: '1px solid rgba(255, 255, 255, 0.85)',
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.8), 0 12px 32px -14px rgba(76,58,150,0.32)',

              '@media (min-width: 900px)': {
                backgroundColor: surfaces.lightPaper,
                backdropFilter: 'blur(14px) saturate(140%)',
                WebkitBackdropFilter: 'blur(14px) saturate(140%)',
              },
            }),
          },
        },
      },

      /**
       * ─────────────────────────────
       * DIALOGS (Modals)
       * Performance-tuned for iOS Safari:
       *  - No backdrop-filter blur (huge GPU cost)
       *  - No inherited Paper radial gradients
       *  - Faster open/close transitions
       * ─────────────────────────────
       */
      MuiDialog: {
        defaultProps: {
          transitionDuration: { enter: 150, exit: 100 },
        },
        styleOverrides: {
          paper: {
            backgroundColor:
              isDark
                ? surfaces.darkDialog
                : surfaces.lightDialog,

            // Override the Paper gradients on dialog surfaces — too heavy on mobile
            backgroundImage: 'none',
            border: 'none',
            backdropFilter: 'none',

            // Beat the light Paper's desktop glass media query
            ...(!isDark && {
              '@media (min-width: 900px)': {
                backgroundColor: surfaces.lightDialog,
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
              },
            }),

            borderRadius: 12,

            boxShadow:
              isDark
                ? '0 24px 80px rgba(0,0,0,0.6)'
                : '0 24px 80px rgba(0,0,0,0.25)',
          },
        },
      },

      // Dialog headers echo the Hub card headings
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: `'Indie Flower', cursive`,
            fontSize: '1.5rem',
            color: isDark ? brand.pink : brand.pinkInk,
            ...(!isDark && {
              backgroundImage: `
                radial-gradient(300px 220px at 20% 20%, rgba(56,189,248,0.22), transparent 60%),
                radial-gradient(280px 220px at 80% 30%, rgba(139,92,246,0.19), transparent 65%),
                radial-gradient(320px 260px at 50% 100%, rgba(236,72,153,0.18), transparent 70%)
              `,
              backgroundColor: 'rgba(255, 255, 255, 0.86)',
            }),
          },
        },
      },

      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor:
              isDark
                ? 'rgba(0,0,0,0.75)'
                : 'rgba(0,0,0,0.45)',
          },
        },
      },

      /**
       * ─────────────────────────────
       * MENUS (Dropdowns / Selects)
       * Same perf tuning — strips heavy gradients
       * and removes the open animation
       * ─────────────────────────────
       */
      MuiMenu: {
        defaultProps: {
          transitionDuration: 0,
        },
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            // Light mode: a solid white card. Without the media query the light
            // Paper's desktop glass wins and posters bleed through grey.
            ...(!isDark && {
              backgroundColor: surfaces.lightDialog,
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
              border: '1px solid rgba(139,92,246,0.14)',
              borderRadius: 12,
              boxShadow: '0 12px 32px -8px rgba(76,58,150,0.28)',
              '@media (min-width: 900px)': {
                backgroundColor: surfaces.lightDialog,
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
              },
            }),
          },
          list: {
            ...(!isDark && {
              padding: 6,
              '& .MuiMenuItem-root': {
                minHeight: 0,
                padding: '8px 14px',
                borderRadius: 8,
                fontSize: 15,
              },
              '& .MuiMenuItem-root:hover': {
                backgroundColor: 'rgba(255,47,146,0.08)',
              },
              '& .MuiDivider-root': {
                margin: '6px 8px',
              },
            }),
          },
        },
      },

      MuiAutocomplete: {
        styleOverrides: {
          paper: isDark
            ? {}
            : {
                backgroundColor: surfaces.lightDialog,
                backgroundImage: 'none',
                backdropFilter: 'none',
              },
        },
      },

      /**
       * ─────────────────────────────
       * LIST ITEM BUTTON (Links hover)
       * ─────────────────────────────
       */
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition:
              'background-color 0.15s ease, color 0.15s ease, transform 0.15s ease',

            '&:hover': {
              transform: 'translateX(4px)',
              color: brand.pink,
            },
          },
        },
      },

      /**
       * ─────────────────────────────
       * BUTTONS
       * ─────────────────────────────
       */
      MuiButton: {
        styleOverrides: {
          /**
           * OUTLINED PRIMARY
           */
          outlinedPrimary: {
            position: 'relative',
            backgroundColor: 'transparent',
            color: brand.pink,

            border: '1px solid transparent',
            borderRadius: 8,
            overflow: 'hidden',

            transition: 'color 0.2s ease',

            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              padding: 1,
              borderRadius: 8,
              background: gradients.primary,

              WebkitMask:
                'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',

              pointerEvents: 'none',
              transition: 'filter 0.2s ease',
            },

            '&:hover': {
              backgroundColor: 'transparent',
              color: brand.pinkHover,
            },

            '&:hover::before': {
              filter: 'brightness(1.15)',
            },

            '&.Mui-disabled': {
              color: isDark ? states.disabledTextDark : states.disabledTextLight,
            },

            '&.Mui-disabled::before': {
              background: isDark ? states.disabledBorderDark : states.disabledBorderLight,
              filter: 'none',
            },
          },

          /**
           * CONTAINED PRIMARY
           */
          containedPrimary: {
            backgroundImage: gradients.primary,
            color: brand.white,

            transition: 'filter 0.2s ease',

            '&:hover': {
              backgroundImage: gradients.primary,
              filter: 'brightness(1.08)',
            },

            '&:active': {
              filter: 'brightness(0.95)',
            },
          },
        },
      },

      /**
       * ─────────────────────────────
       * APP BAR (Header)
       * ─────────────────────────────
       */
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'transparent' : surfaces.darkBar,
            // Light mode keeps the dark mode header: the dark Paper glows,
            // screened over a dark ground.
            ...(!isDark && {
              backgroundImage: `
                radial-gradient(400px 260px at 20% 20%, rgba(56,189,248,0.45), transparent 60%),
                radial-gradient(380px 280px at 80% 30%, rgba(139,92,246,0.40), transparent 65%),
                radial-gradient(420px 320px at 50% 85%, rgba(236,72,153,0.35), transparent 70%)
              `,
              backgroundBlendMode: 'screen',
              color: brand.white,
              border: 'none',
              // Undo the Paper glass: blur turns the 1px gradient line grey
              backdropFilter: 'none',
              '@media (min-width: 900px)': {
                backgroundColor: surfaces.darkBar,
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
              },
            }),
            boxShadow: 'none',
            position: 'relative',

            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '1px',
              background: gradients.primary,
              opacity: 0.9,
            },
          },
        },
      },
    },
  });
};
